import React from 'react';
import { User, ShieldCheck } from 'lucide-react';

interface Props {
  name: string;
  photoUrl?: string | null;
  size?: 'sm' | 'md' | 'lg';
  pending?: boolean;
  verified?: boolean;
}

const sizeMap = {
  sm: 'w-12 h-12 text-base',
  md: 'w-16 h-16 text-lg',
  lg: 'w-24 h-24 text-2xl',
};

const colorFromName = (name: string) => {
  const palettes = [
    'from-pink-400 to-fuchsia-500',
    'from-purple-400 to-indigo-500',
    'from-amber-400 to-orange-500',
    'from-emerald-400 to-teal-500',
    'from-sky-400 to-blue-500',
    'from-rose-400 to-red-500',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return palettes[Math.abs(hash) % palettes.length];
};

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || '')
    .join('');

const MemberAvatar: React.FC<Props> = ({ name, photoUrl, size = 'md', pending, verified }) => {
  const cls = sizeMap[size];
  return (
    <div className="relative inline-block">
      {photoUrl ? (
        <img
          src={photoUrl}
          alt={name}
          className={`${cls} rounded-full object-cover border-2 border-white shadow-md ${
            pending ? 'opacity-70 grayscale' : ''
          }`}
        />
      ) : (
        <div
          className={`${cls} rounded-full bg-gradient-to-br ${colorFromName(
            name
          )} flex items-center justify-center text-white font-bold border-2 border-white shadow-md ${
            pending ? 'opacity-70' : ''
          }`}
        >
          {initials(name) || <User className="w-1/2 h-1/2" />}
        </div>
      )}
      {pending && (
        <span className="absolute -bottom-1 -right-1 px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[9px] font-bold shadow">
          Pending
        </span>
      )}
      {verified && !pending && (
        <span
          className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-emerald-500 text-white shadow flex items-center justify-center border-2 border-white"
          title="Verified relationship"
        >
          <ShieldCheck className="w-3 h-3" />
        </span>
      )}
    </div>
  );
};

export default MemberAvatar;
